import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  CurrencyPaymentsQueryParams,
  CurrencyPaymentsQueryResponse,
  useCurrencyPaymentsQuery,
} from '@/hooks/currencyPayments/useCurrencyPaymentsQuery'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { storage } from '@/lib/utils'
import {
  CurrencyPaymentsFilters,
  useCurrencyPaymentsFilters,
} from '../hooks/useCurrencyPaymentsFilters'

// Define the context type
interface CurrencyPaymentsTableContextType {
  currentPage: number
  setCurrentPage: (_page: number) => void
  pageSize: number
  setPageSize: (_size: number) => void
  currencyPaymentsData: CurrencyPaymentsQueryResponse | null
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  refetch: () => void
  goToNextPage: () => void
  goToPreviousPage: () => void
  skeletonRowCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

// Create the context with a default value
const TableContext = createContext<
  CurrencyPaymentsTableContextType | undefined
>(undefined)

// Default values
export const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 20

// Storage keys
export const CURRENCY_PAYMENTS_STORAGE_KEYS = {
  CURRENT_PAGE: 'currency-payments-table-current-page',
  PAGE_SIZE: 'currency-payments-table-page-size',
} as const

// Helper to create a filter key for comparison
const createFilterKey = (filters: CurrencyPaymentsFilters): string => {
  return JSON.stringify({
    codeCurrency: filters.codeCurrency || '',
    amountFrom: filters.amountFrom || '',
    amountTo: filters.amountTo || '',
    economicCycleIds: filters.economicCycleIds || '',
    paymentGatewayIds: filters.paymentGatewayIds || '',
    dateFrom: filters.dateFrom || '',
    dateTo: filters.dateTo || '',
    areaSalesId: filters.areaSalesId || '',
  })
}

export const TableContextProvider = ({ children }: { children: ReactNode }) => {
  // Get filters from the billing filters hook (now with built-in localStorage persistence)
  const { filters } = useCurrencyPaymentsFilters()

  const { businessData } = useBusinessDataQuery({})

  // Track previous filter state to detect changes
  const previousFiltersRef = useRef<string>('')
  const isInitialMount = useRef(true)

  const [currentPage, setCurrentPageState] = useState(() =>
    storage.get(CURRENCY_PAYMENTS_STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
  )
  const [pageSize, setPageSizeState] = useState(() =>
    storage.get(CURRENCY_PAYMENTS_STORAGE_KEYS.PAGE_SIZE, INITIAL_PAGE_SIZE)
  )

  // Check if filters have changed (excluding pagination-related changes)
  const currentFilterKey = createFilterKey(filters)
  const filtersHaveChanged =
    previousFiltersRef.current !== currentFilterKey && !isInitialMount.current

  // Update previous filters reference
  useEffect(() => {
    previousFiltersRef.current = currentFilterKey
    isInitialMount.current = false
  }, [currentFilterKey])

  // Reset to page 1 when filters change (but not on initial mount)
  useEffect(() => {
    if (filtersHaveChanged && currentPage !== INITIAL_PAGE) {
      setCurrentPageState(INITIAL_PAGE)
      storage.set(CURRENCY_PAYMENTS_STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
    }
  }, [filtersHaveChanged, currentPage])

  const queryParams: CurrencyPaymentsQueryParams = {
    page: currentPage,
    per_page: pageSize,
    ...(filters.codeCurrency !== 'CUP'
      ? { codeCurrency: filters.codeCurrency }
      : {}),
    ...(filters.dateFrom || filters.dateTo
      ? {
          dateFrom: filters.dateFrom || null,
          dateTo: filters.dateTo || null,
        }
      : {}),
    ...(filters.amountFrom || filters.amountTo
      ? {
          amountFrom: filters.amountFrom,
          amountTo: filters.amountTo,
          codeCurrency: filters.codeCurrency || 'CUP',
        }
      : {}),
    ...(Array.isArray(filters.economicCycleIds) &&
    filters.economicCycleIds.length > 0
      ? { economicCycleIds: filters.economicCycleIds.join(',') }
      : {}),

    ...(Array.isArray(filters.paymentGatewayIds) &&
    filters.paymentGatewayIds.length > 0
      ? { paymentGatewayIds: filters.paymentGatewayIds.join(',') }
      : {}),
    ...(filters.areaSalesId
      ? { areaSalesId: filters.areaSalesId.toString() }
      : {}),

    ...(filters.paymentConfirmed ? { paymentConfirmed: true } : {}),
    ...(filters.isARefund ? { isARefund: true } : {}),
    ...(filters.isPrepaidPayment ? { isPrepaidPayment: true } : {}),
    ...(filters.isPartialPayment ? { isPartialPayment: true } : {}),
  }

  const {
    data: currencyPaymentsData,
    isLoading,
    isFetching,
    isError,
    refetch,
    pagination,
  } = useCurrencyPaymentsQuery({
    params: queryParams,
    options: {
      staleTime: 5 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: true,
    },
    businessId: businessData?.id ?? 0,
  })

  const setCurrentPage = useCallback((page: number) => {
    setCurrentPageState(page)
    storage.set(CURRENCY_PAYMENTS_STORAGE_KEYS.CURRENT_PAGE, page)
  }, [])

  const setPageSize = useCallback(
    (size: number) => {
      setPageSizeState(size)
      storage.set(CURRENCY_PAYMENTS_STORAGE_KEYS.PAGE_SIZE, size)
      if (currentPage !== INITIAL_PAGE) setCurrentPage(INITIAL_PAGE)
    },
    [currentPage, setCurrentPage]
  )

  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) setCurrentPage(currentPage + 1)
  }, [currentPage, setCurrentPage, pagination.hasNextPage])

  const goToPreviousPage = useCallback(() => {
    if (pagination.hasPreviousPage) setCurrentPage(currentPage - 1)
  }, [currentPage, setCurrentPage, pagination.hasPreviousPage])

  const contextValue: CurrencyPaymentsTableContextType = {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    currencyPaymentsData: currencyPaymentsData || null,
    isLoading,
    isFetching,
    isError,
    refetch,
    goToNextPage,
    goToPreviousPage,
    skeletonRowCount: pageSize,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
  }

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  )
}

export const useTableContext = () => {
  const context = useContext(TableContext)
  if (context === undefined) {
    throw new Error(
      'useTableContext must be used within a TableContextProvider'
    )
  }
  return context
}
